import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo, useRef } from "react";

import { SearchQueryPredicate } from "types/generated";

import { GET_SPACELIFT_VCS_FILES } from "./gql";

type UseSearchSpaceliftVcsFilesProps = {
  repoId: string | undefined;
  commit?: string;
  predicates?: SearchQueryPredicate[];
  fullTextSearch?: string;
  skip?: boolean;
};

const useSearchSpaceliftVcsFiles = ({
  repoId,
  commit,
  predicates,
  fullTextSearch,
  skip,
}: UseSearchSpaceliftVcsFilesProps) => {
  const fetchingRef = useRef(false);

  const { data, loading, error, previousData, fetchMore, refetch } = useQuery(GET_SPACELIFT_VCS_FILES, {
    variables: {
      repoID: repoId || "",
      commit: commit || null,
      input: {
        after: null,
        first: 50,
        fullTextSearch: fullTextSearch || null,
        orderBy: null,
        predicates: predicates ?? null,
        requestedPage: null,
      },
    },
    skip: skip || !repoId,
  });

  const files = useMemo(() => {
    const edges = data?.searchRepoFiles.edges || previousData?.searchRepoFiles.edges;
    return edges?.map((edge) => edge.node) || [];
  }, [data, previousData]);

  const pageInfo = data?.searchRepoFiles.pageInfo || previousData?.searchRepoFiles.pageInfo;
  const hasMore = !!pageInfo?.hasNextPage;

  const loadMore = useCallback(() => {
    if (fetchingRef.current || !hasMore) return;

    fetchingRef.current = true;

    fetchMore({
      variables: {
        input: {
          after: pageInfo?.endCursor,
          first: 50,
          fullTextSearch: fullTextSearch || null,
          orderBy: null,
          predicates: null,
          requestedPage: null,
        },
      },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previous;

        return {
          searchRepoFiles: {
            ...fetchMoreResult.searchRepoFiles,
            edges: [...previous.searchRepoFiles.edges, ...fetchMoreResult.searchRepoFiles.edges],
          },
        };
      },
    }).finally(() => {
      fetchingRef.current = false;
    });
  }, [fetchMore, fullTextSearch, hasMore, pageInfo?.endCursor]);

  return {
    files,
    loading,
    error,
    pageInfo,
    hasMore,
    loadMore,
    refetch,
  };
};

export default useSearchSpaceliftVcsFiles;
